import { Box, Cards, CardsProps, Spinner } from "@amzn/awsui-components-react";
import { getString } from "common/ui_string_labels/ui-strings-utils";
import { ErrorProps } from "interfaces";
import { JSX, ReactNode } from "react";
import CustomAlert from "../CustomAlert/CustomAlert";
import { AlertProps } from "@amzn/awsui-components-react";
import { CARD_TABLE_TEST_ID } from "helpers";
import InfiniteScroll from "react-infinite-scroll-component";

const getStringPath: string = "common.component.CardTable";

/**
 * Renders an empty state message when no hardware items are available to display
 *
 * @returns {JSX.Element} A centered box containing a "no content" message
 */
export const CardEmptyState = (): JSX.Element => (
    <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
        <b>{getString(`${getStringPath}.noContent`)}</b>
    </Box>
);

/**
 * Represent the props for the CardTableProps component
 *
 * @interface
 * @property {CardsProps.CardDefinition<T>} cardDefinitions - An object containing card definitions
 * @property {T[]} catalog - An array of card information
 * @property {CardSectionType[]} cardSectionsVisible - An array of visible card sections
 * @property {ReactNode} header - An optional header component
 * @property {boolean} loading - A flag indicating if the data is loading
 * @property {string} trackBy - The property to track by
 * @property {ErrorProps} error - An optional error object
 * @property {CardsProps.CardsLayout[]} cardsLayout - An array representing the cards layout
 * @property {boolean} hasMore - A boolean indicating whether there are more items to load
 * @property {() => void} onLoadMore - A callback function to load more items
 * @property {boolean} isLoadingMore - A boolean indicating whether more items are currently being loaded
 */
export interface CardTableProps<T> {
    cardDefinitions: CardsProps.CardDefinition<T>;
    catalog: T[];
    cardSectionsVisible: string[];
    header?: ReactNode;
    loading?: boolean;
    trackBy: string;
    error?: ErrorProps;
    cardsLayout: CardsProps.CardsLayout[];
    hasMore?: boolean;
    onLoadMore?: () => void;
    isLoadingMore?: boolean;
}

/**
 * Renders a table of items using cards with infinite scrolling
 *
 * @param {CardsProps.CardDefinition<T>} props.cardDefinitions - Defines the structure and layout of each card
 * @param {T[]} props.catalog - Array of items to display in the cards
 * @param {string[]} props.cardSectionsVisible - Array of section IDs that should be visible
 * @param {ReactNode} props.header - Optional header content to display above the cards
 * @param {boolean} props.hasMore - Whether there are more items to load for infinite scrolling
 * @param {() => void} props.onLoadMore - Callback to load more items for infinite scrolling
 * @returns {JSX.Element} A responsive grid of cards displaying the catalog items
 */
const CardTable = <T,>({
    cardDefinitions,
    catalog,
    loading = false,
    cardSectionsVisible,
    header,
    trackBy,
    error,
    cardsLayout,
    hasMore = false,
    onLoadMore,
}: CardTableProps<T>) => {
    const errorAlertType: AlertProps.Type = "error";

    const cardsComponent = (
        <Cards<T>
            data-testid={CARD_TABLE_TEST_ID}
            cardDefinition={cardDefinitions}
            cardsPerRow={cardsLayout}
            loading={loading}
            items={catalog}
            loadingText={getString(`${getStringPath}.loadingResources`)}
            variant="full-page"
            trackBy={trackBy}
            visibleSections={cardSectionsVisible}
            empty={
                error ? (
                    <CustomAlert type={errorAlertType} title={error.name} content={error.content} />
                ) : (
                    <CardEmptyState />
                )
            }
            header={header}
        />
    );

    if (!loading && hasMore && onLoadMore) {
        return (
            <InfiniteScroll
                dataLength={catalog.length}
                next={onLoadMore}
                hasMore={hasMore}
                loader={
                    <Box textAlign="center" padding="m">
                        <Spinner size="normal" />
                    </Box>
                }
            >
                {cardsComponent}
            </InfiniteScroll>
        );
    }

    return cardsComponent;
};

export default CardTable;
